import { Link } from 'react-router-dom';
import { ENTITIES } from '../data';

type LayerTint = 'rust' | 'accent' | 'accent-soft' | 'neutral';

interface LayerNode {
  id: string;
  wide?: boolean;
  many?: boolean;
}

interface Layer {
  title: string;
  side: string;
  nodes: LayerNode[];
  tint: LayerTint;
}

const LAYERS: Layer[] = [
  {
    title: 'The log of truth',
    side: 'Analytics read from here',
    nodes: [{ id: 'lifecycleEvent', wide: true }, { id: 'invoice' }],
    tint: 'rust',
  },
  {
    title: 'The agreement',
    side: 'One customer, one plan, pinned versions',
    nodes: [{ id: 'client' }, { id: 'subscription', wide: true }, { id: 'expansion' }],
    tint: 'accent',
  },
  {
    title: 'The private deal',
    side: 'Per-customer override at next renewal',
    nodes: [{ id: 'customPricingAgreement', wide: true }],
    tint: 'accent-soft',
  },
  {
    title: 'The price tag',
    side: 'Variable Components priced per scope',
    nodes: [
      { id: 'variableComponent', wide: true },
      { id: 'variableComponentPrice' },
      { id: 'region' },
    ],
    tint: 'accent-soft',
  },
  {
    title: 'The package',
    side: 'Versioned plans customers buy',
    nodes: [{ id: 'bundle', wide: true }],
    tint: 'neutral',
  },
  {
    title: 'The capability',
    side: 'Versioned groups of flags + components',
    nodes: [{ id: 'module', wide: true, many: true }],
    tint: 'neutral',
  },
  {
    title: 'The atoms',
    side: 'Switches and metrics',
    nodes: [
      { id: 'featureFlag', many: true },
      { id: 'meter', many: true },
    ],
    tint: 'neutral',
  },
];

export function LayersSchema() {
  return (
    <div className="layers-schema">
      <div className="layers-title-row">
        <div>
          <span className="rv-eyebrow-mini">Bottom-up</span>
          <h3>Every sale is a stack.</h3>
          <p>
            Feature Flags and Meters are the atoms. Variable Components wrap a pricing model
            around a Meter. Modules and Bundles compose Components into versioned plans.
            Subscriptions pin specific versions. Custom Pricing Agreements override a single
            component for a single customer. Everything writes events as it moves.
          </p>
        </div>
      </div>

      <div className="layers-stack">
        {LAYERS.map((layer, li) => (
          <div className={`layer layer-${layer.tint}`} key={li}>
            <div className="layer-side">
              <div className="layer-num">L{LAYERS.length - li}</div>
              <div className="layer-title">{layer.title}</div>
              <div className="layer-sub">{layer.side}</div>
            </div>
            <div className="layer-nodes">
              {layer.nodes.map((n, ni) => {
                const e = ENTITIES[n.id];
                return (
                  <Link
                    key={ni}
                    className={`layer-node ${n.wide ? 'layer-node-wide' : ''}`}
                    to={`/docs/concepts/${n.id}`}
                  >
                    <div className="layer-node-name">
                      {e.name}
                      {n.many && <span className="layer-node-many">many</span>}
                    </div>
                    <div className="layer-node-line">{e.oneLiner}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="layers-footnote">
        <span className="layers-arrow">↑</span>
        <div>
          <strong>Built once, sold many times.</strong>
          <span>
            You configure the bottom four layers during onboarding. The top three happen every
            time a customer signs, upgrades, renegotiates, or renews.
          </span>
        </div>
      </div>
    </div>
  );
}
